import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import actions from '@src/redux/actions';
import {
  AUTO_SAVE_STATUS,
  AUTO_SAVE_FLUSH_TIMEOUT_MS,
} from '@src/constants/project';

// Statuses that end a flush wait. SAVED → success; ERROR/BLOCKED → failure.
const SETTLED_STATUSES = [
  AUTO_SAVE_STATUS.SAVED,
  AUTO_SAVE_STATUS.ERROR,
  AUTO_SAVE_STATUS.BLOCKED,
];

// Already-clean states: there is nothing pending to persist, so a flush would be
// a no-op. IDLE → no edits since the last save; SAVED → the last save just
// finished. (After any edit the status is SAVING straight away, so neither of
// these can hide unsaved work.)
const CLEAN_STATUSES = [AUTO_SAVE_STATUS.IDLE, AUTO_SAVE_STATUS.SAVED];

// Immediately triggers an auto-save pass (bypassing the debounce) and resolves
// once it settles. Returns true when the project reached SAVED, false on
// ERROR/BLOCKED or if it does not settle within AUTO_SAVE_FLUSH_TIMEOUT_MS.
// Used before credit-spending actions (e.g. synthesis) so they never run on
// content that has not been persisted yet.
const useAutoSaveFlush = () => {
  const store = useStore();
  const dispatch = useDispatch();

  const flushAndWaitForSave = useCallback(
    () =>
      new Promise((resolve) => {
        // Everything is already persisted — resolve immediately so the caller
        // (synthesis / AI features) proceeds without a meaningless loading flash.
        const currentStatus = store.getState().project.autoSaveStatus;
        if (CLEAN_STATUSES.includes(currentStatus)) {
          resolve(true);
          return;
        }

        let settled = false;
        let unsubscribe = () => {};
        let timer;

        const finish = (saved) => {
          if (settled) return;
          settled = true;
          unsubscribe();
          clearTimeout(timer);
          resolve(saved);
        };

        // Subscribe before dispatching so we never miss the transition, even
        // when the save settles synchronously (e.g. nothing dirty → SAVED).
        // Resolves on the first settled status, then unsubscribes — so the
        // listener lives only for the short flush window, not the whole
        // synthesis run.
        unsubscribe = store.subscribe(() => {
          const status = store.getState().project.autoSaveStatus;
          if (SETTLED_STATUSES.includes(status)) {
            finish(status === AUTO_SAVE_STATUS.SAVED);
          }
        });

        timer = setTimeout(() => finish(false), AUTO_SAVE_FLUSH_TIMEOUT_MS);

        dispatch(actions.project.autoSaveTrigger());
      }),
    [store, dispatch],
  );

  return { flushAndWaitForSave };
};

export default useAutoSaveFlush;
